import { API_BASE_URL } from "../config/env";

// Driver onboarding orchestrates PUBLIC driver endpoints (send/verify OTP,
// register) plus the driver-authenticated documents step. It deliberately does
// NOT go through the admin axios instance (which attaches the admin token and
// redirects on 401) — register returns a *driver* token we use for documents.
async function req(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
): Promise<any> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    const m = json?.message;
    const msg =
      (m && typeof m === "object" ? m.en : m) ||
      json?.error ||
      `Request failed (${res.status})`;
    throw new Error(typeof msg === "string" ? msg : "Request failed");
  }
  return json.data;
}

export interface DriverRegisterBody {
  personalDetails: {
    name: string;
    email: string;
    phoneNumber: string;
    gender: string;
    dob: string; // DD-MM-YYYY
    profilePicUrl: string;
  };
  vehicleDetails: {
    make: string;
    model: string;
    color: string;
    latraVehicleType: string;
    vehicleImages: string[];
    /** Plate number — a vehicle attribute, not a document. */
    registrationNumber?: string;
  };
  /** Sign-in password (min 6). Required since drivers sign in with one. */
  password: string;
  languagePreference: string;
  termsAndConditionAcceptVersion: string;
}

export const onboardingApi = {
  sendOtp: (phoneNumber: string) =>
    req("POST", "/auth/driver/send-otp", { phoneNumber }),

  verifyOtp: (phoneNumber: string, otp: string, pinId: string) =>
    req("POST", "/auth/driver/verify-otp", { phoneNumber, otp, pinId }),

  register: (
    body: DriverRegisterBody,
  ): Promise<{ driver: { _id: string }; tokens: { accessToken: string } }> =>
    req("POST", "/driver/register", body),

  // Submit the document set (license, LATRA D4, insurance — per
  // DocumentDetailsSchema). Saves progressively; does not by itself move the
  // driver to UNDER_APPROVAL (that's the separate submit-for-approval step).
  submitDocuments: (driverToken: string, documents: Record<string, unknown>) =>
    req("POST", "/driver/submit-documents", { documents }, driverToken),

  async termsVersion(): Promise<string | undefined> {
    const s = await req("GET", "/settings");
    return s?.driverAppTermsAndConditions?.current?.version;
  },
};
